// src/Components/ResponseContainer/index.js
import React, { useRef, useEffect } from 'react';
import { useChatContext } from '../../Context/ChatContext';
import { useTheme } from '../../Context/ThemeContext';
import ResponseItem from '../ResponseItem';
import './index.css';

const ResponseContainer = () => {
    const { messages, selectedQuestionId, isLoading, error, currentDateBeingViewed } = useChatContext();
    const { isDarkMode } = useTheme();
    const messagesEndRef = useRef(null);
    const containerRef = useRef(null);

    // Scroll to selected question effect (remains the same)
    // it is used to scroll to the selected question
    // it is used to highlight the selected question
    // it is used to remove the highlight from the previous selected question
    // it is used to highlight the selected question for 2.5 seconds
    useEffect(() => {
        if (!selectedQuestionId || !containerRef.current) return;
        const container = containerRef.current;
        const element = document.getElementById(`chat-turn-${selectedQuestionId}`);
        if (!element) return;

        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // If fully visible, only highlight
        const fullyVisible = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
        if (fullyVisible) {
            element.classList.add('highlight-turn');
            const t = setTimeout(() => { if (element) element.classList.remove('highlight-turn'); }, 2500);
            return () => clearTimeout(t);
        }

        // Align top, but clamp to valid range
        const offset = elementRect.top - containerRect.top;
        const target = container.scrollTop + offset;
        const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
        const clamped = Math.min(Math.max(0, target), maxScroll);
        container.scrollTo({ top: clamped, behavior: 'smooth' });

        element.classList.add('highlight-turn');
        const timer = setTimeout(() => { if (element) element.classList.remove('highlight-turn'); }, 2500);
        return () => clearTimeout(timer);
    }, [selectedQuestionId]);

    // Auto-scroll to bottom effect (remains the same)
    // it is used to auto-scroll to the bottom of the chat when a new message is added
    // it is used to auto-scroll to the bottom of the chat when the chat is cleared
    // it is used to auto-scroll to the bottom of the chat when the chat is loade
    useEffect(() => {
        if (selectedQuestionId || !containerRef.current) return;
        const container = containerRef.current;
        const timer = setTimeout(() => {
            const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
            container.scrollTo({ top: maxScroll, behavior: 'smooth' });
        }, 0);
        return () => clearTimeout(timer);
    }, [messages, selectedQuestionId]);

    // it is used to check if the messages are an array
    // it is used to return an empty array if the messages are not an array
    const safeMessages = Array.isArray(messages) ? messages : [];

    // it is used to check if the messages are loading
    // it is used to check if the messages are empty
    // it is used to check if the current date being viewed is not null
    // it is used to set the status message
    let statusMessage = null;
    if (isLoading && safeMessages.length === 0 && !currentDateBeingViewed) {
        statusMessage = "Initializing Chat...";
    } else if (isLoading && safeMessages.length === 0 && currentDateBeingViewed) {
        statusMessage = `Loading messages for ${new Date(currentDateBeingViewed + "T00:00:00").toLocaleDateString()}...`;
    } else if (error && safeMessages.length === 0) {
        statusMessage = `Error: ${error}`;
    }
    // IMPORTANT: Only set specific "no messages for date" if a date is actually being viewed. 
    // Otherwise, if safeMessages is empty and no loading/error, it means it's truly the initial empty state.
    else if (safeMessages.length === 0 && currentDateBeingViewed && !isLoading && !error) {
         statusMessage = `No messages for ${new Date(currentDateBeingViewed + "T00:00:00").toLocaleDateString()}.`;
    }
    // No `else if (safeMessages.length === 0)` here, we handle that case below.

    // If there's a status message (loading/error/no messages for specific date), display it.
    if (statusMessage) {
        return (
            <div className={`response-container ${isDarkMode ? 'dark-mode' : ''}`} ref={containerRef}>
                <div className={`empty-state status-message-state ${isDarkMode ? 'dark-mode' : ''}`}>
                    <p>{statusMessage}</p>
                </div>
            </div>
        );
    }

    // If there are no messages and no status message (truly empty initial state)
    // return null or a minimal placeholder that Home.js can use to show the "How can I help you?" prompt.
    if (safeMessages.length === 0) {
        return null; // Or a specific minimal placeholder if Home.js needs to detect this state differently
        // Example placeholder:
        // return <div className="response-container-hidden-placeholder"></div>;
    }

    // If we have messages, render them
    return (
        <div className={`response-container ${isDarkMode ? 'dark-mode' : ''}`} ref={containerRef}>
            {safeMessages.map((item) => (
                <div
                    key={item.clientId || item.id || item.history_id}
                    id={`chat-turn-${item.id}`}
                    className={`chat-turn ${item.is_user_message ? 'user-turn' : 'bot-turn'}`}
                >
                    <ResponseItem item={item} />
                    <div className="message-meta">
                        {/* {<span className="timestamp">
                            {item.timestamp ? new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>} */}
                        {(item.status && item.status !== 'sent' && item.status !== 'answered' && item.status !== 'pending_answer' && item.status !== 'error') && (
                            <span className={`status-indicator status-${item.status}`}>({item.status})</span>
                        )}
                        {item.status === 'error' && (
                           <span className="status-indicator status-error">(Failed)</span>
                        )}
                    </div>
                </div>
            ))}
            <div ref={messagesEndRef} style={{ height: 0, flex: '0 0 0' }} />
        </div>
    );
};

export default ResponseContainer;